(() => {
  if (window.__seekCustomCartLoaded) return;
  window.__seekCustomCartLoaded = true;

  const root = window.Shopify?.routes?.root || "/";
  const announce = (message) => {
    const status = document.getElementById("status-announcement");
    if (status) status.textContent = message;
  };

  const sectionsForCartRequest = () => {
    const sections = [];
    document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
      bubbles: true,
      detail: { sections }
    }));
    return [...new Set(sections)];
  };

  const requestCartChange = async (endpoint, payload, baseEvent) => {
    payload.sections = sectionsForCartRequest();
    payload.sections_url = window.location.pathname;

    const response = await fetch(`${root}cart/${endpoint}.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
      body: JSON.stringify(payload)
    });

    const cart = await response.json();
    if (!response.ok) throw new Error(cart.description || "Cart update failed");

    document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
      bubbles: true,
      detail: { baseEvent, cart }
    }));
    return cart;
  };

  class CustomCartPlan extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.addEventListener("change", this.onChange.bind(this), { signal: this.abortController.signal });
      this.addEventListener("click", this.onClick.bind(this), { signal: this.abortController.signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    async onChange(event) {
      const select = event.target.closest("[data-plan-select]");
      if (!select) return;
      await this.updatePlan(select.value || null);
    }

    async onClick(event) {
      const button = event.target.closest("[data-subscribe-upsell]");
      if (!button) return;
      await this.updatePlan(button.dataset.subscribeUpsell);
    }

    async updatePlan(sellingPlan) {
      const error = this.querySelector(".custom-cart-plan__error");
      const controls = this.querySelectorAll("button, input, select");
      error.hidden = true;
      this.setAttribute("aria-busy", "true");
      controls.forEach((control) => { control.disabled = true; });

      try {
        await requestCartChange("change", {
          line: Number(this.dataset.line),
          quantity: Number(this.dataset.quantity),
          selling_plan: sellingPlan || null
        }, "variant:add");
        announce(sellingPlan ? "Subscription option updated" : "Changed to one-time purchase");
      } catch (err) {
        error.hidden = false;
        announce(err.message);
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      } finally {
        this.removeAttribute("aria-busy");
        controls.forEach((control) => { control.disabled = false; });
      }
    }
  }

  class CustomCartPack extends HTMLElement {
    connectedCallback() {
      this.abortController?.abort();
      this.abortController = new AbortController();
      this.addEventListener("click", this.onClick.bind(this), { signal: this.abortController.signal });
    }

    disconnectedCallback() {
      this.abortController?.abort();
    }

    async onClick(event) {
      const button = event.target.closest("[data-pack-variant]");
      if (!button || button.disabled || this.hasAttribute("aria-busy")) return;

      const error = this.querySelector(".custom-cart-pack__error");
      const controls = Array.from(this.querySelectorAll("button"));
      const disabledStates = controls.map((control) => control.disabled);
      const targetVariant = Number(button.dataset.packVariant);
      const targetSellingPlan = button.dataset.packSellingPlan;
      let properties = {};

      try {
        properties = JSON.parse(this.dataset.properties || "{}");
      } catch (_) {}

      error.hidden = true;
      this.setAttribute("aria-busy", "true");
      controls.forEach((control) => { control.disabled = true; });

      try {
        const item = {
          id: targetVariant,
          quantity: Number(this.dataset.quantity) || 1
        };
        if (targetSellingPlan) item.selling_plan = Number(targetSellingPlan);
        if (properties && !Array.isArray(properties) && Object.keys(properties).length) item.properties = properties;

        const addResponse = await fetch(`${root}cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify({ items: [item] })
        });
        const addedItem = await addResponse.json();
        if (!addResponse.ok) throw new Error(addedItem.description || "Pack upgrade failed");

        const cartAfterAddResponse = await fetch(`${root}cart.js`, {
          headers: { "X-Requested-With": "XMLHttpRequest" },
          cache: "no-store"
        });
        const cartAfterAdd = await cartAfterAddResponse.json();
        if (!cartAfterAddResponse.ok) throw new Error(cartAfterAdd.description || "Could not refresh the cart");

        const currentVariant = Number(this.dataset.currentVariant);
        const currentSellingPlan = String(this.dataset.currentSellingPlan || "");
        const propertyEntries = (value) => Object.entries(value || {}).sort(([first], [second]) => first.localeCompare(second));
        const expectedProperties = JSON.stringify(propertyEntries(properties));
        const originalLine = cartAfterAdd.items.find((item) => item.key === this.dataset.lineKey) || cartAfterAdd.items.find((item) => {
          const itemSellingPlan = String(item.selling_plan_allocation?.selling_plan?.id || "");
          return item.id === currentVariant
            && itemSellingPlan === currentSellingPlan
            && JSON.stringify(propertyEntries(item.properties)) === expectedProperties;
        });
        if (!originalLine) throw new Error("Could not locate the original pack in the cart");

        await requestCartChange("change", {
          id: originalLine.key,
          quantity: 0
        }, "variant:add");
        announce(`Pack updated to ${button.dataset.packTitle}`);
      } catch (err) {
        error.hidden = false;
        announce(err.message);
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      } finally {
        this.removeAttribute("aria-busy");
        controls.forEach((control, index) => { control.disabled = disabledStates[index]; });
      }
    }
  }

  class CartBundle extends HTMLElement {
    connectedCallback() {
      this.querySelector("[data-bundle-remove]")?.addEventListener("click", () => this.removeBundle(), { once: true });
    }

    async removeBundle() {
      const button = this.querySelector("[data-bundle-remove]");
      const error = this.querySelector(".custom-cart-bundle__error");
      let keys = [];
      try { keys = JSON.parse(this.dataset.lineKeys || "[]"); } catch (_) {}
      if (!keys.length) return;

      const updates = Object.fromEntries(keys.map((key) => [key, 0]));
      button.disabled = true;
      this.setAttribute("aria-busy", "true");
      error.hidden = true;

      try {
        await requestCartChange("update", { updates }, "variant:add");
        announce("Bundle removed from cart");
      } catch (err) {
        error.hidden = false;
        button.disabled = false;
        this.removeAttribute("aria-busy");
        announce(err.message);
      }
    }
  }

  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      this.load();
    }

    disconnectedCallback() {
      this.paginationController?.abort();
    }

    async load() {
      let productIds = [];
      let excludedProductIds = [];
      try { productIds = JSON.parse(this.dataset.productIds || "[]"); } catch (_) {}
      try { excludedProductIds = JSON.parse(this.dataset.excludedProductIds || "[]"); } catch (_) {}
      productIds = [...new Set(productIds.map(String).filter(Boolean))].slice(0, 3);
      excludedProductIds = [...new Set(excludedProductIds.map(String).filter(Boolean))];
      if (!productIds.length || !this.dataset.url) return;

      const limit = Math.min(10, Math.max(1, Number(this.dataset.limit) || 8));
      // Seed with cart product IDs so recommendations never repeat an item
      // that the customer already has in their cart, or an item explicitly
      // excluded in the cart drawer settings.
      const selectedProducts = new Set([...productIds, ...excludedProductIds]);
      const selectedCards = [];

      const collect = (cards) => {
        cards.forEach((card) => {
          const productId = card.dataset.recommendationProductId;
          if (!productId || selectedProducts.has(productId)) return;
          selectedProducts.add(productId);
          selectedCards.push(card);
        });
      };

      try {
        const complementary = await this.fetchForProducts(productIds, "complementary");
        complementary.forEach(collect);

        if (selectedCards.length < limit) {
          const related = await this.fetchForProducts(productIds, "related");
          related.forEach(collect);
        }

        if (!this.isConnected || !selectedCards.length) return;
        this.recommendationCards = selectedCards
          .sort((first, second) => Number(second.dataset.recommendationPrice || 0) - Number(first.dataset.recommendationPrice || 0))
          .slice(0, limit);
        this.renderPages();
        this.hidden = false;
      } catch (_) {
        this.hidden = true;
      }
    }

    renderPages() {
      if (!this.recommendationCards?.length) return;

      const list = this.querySelector("[data-recommendation-list]");
      const viewport = this.querySelector("[data-recommendation-viewport]");
      if (!list || !viewport) return;

      const pageSize = 2;
      const pages = [];

      for (let index = 0; index < this.recommendationCards.length; index += pageSize) {
        const page = document.createElement("div");
        page.className = "custom-cart__recommendation-page";
        page.setAttribute("role", "group");
        page.setAttribute("aria-label", `Recommendation page ${pages.length + 1}`);
        page.append(...this.recommendationCards.slice(index, index + pageSize).map((card) => document.importNode(card, true)));
        pages.push(page);
      }

      list.replaceChildren(...pages);
      viewport.scrollLeft = 0;
      this.setupPagination(pages.length);
    }

    setupPagination(pageCount) {
      const viewport = this.querySelector("[data-recommendation-viewport]");
      const navigation = this.querySelector("[data-recommendation-nav]");
      const previous = this.querySelector("[data-recommendation-previous]");
      const next = this.querySelector("[data-recommendation-next]");
      const status = this.querySelector("[data-recommendation-status]");
      if (!viewport || !navigation || !previous || !next || !status) return;

      this.paginationController?.abort();
      this.paginationController = new AbortController();
      const listenerOptions = { signal: this.paginationController.signal };

      let currentPage = 0;
      const update = () => {
        const pageWidth = viewport.clientWidth || 1;
        currentPage = Math.min(pageCount - 1, Math.max(0, Math.round(viewport.scrollLeft / pageWidth)));
        previous.disabled = currentPage === 0;
        next.disabled = currentPage === pageCount - 1;
        status.textContent = `${currentPage + 1} / ${pageCount}`;
      };
      const goToPage = (page) => {
        viewport.scrollTo({ left: page * viewport.clientWidth, behavior: "smooth" });
      };

      navigation.hidden = pageCount <= 1;
      previous.addEventListener("click", () => goToPage(Math.max(0, currentPage - 1)), listenerOptions);
      next.addEventListener("click", () => goToPage(Math.min(pageCount - 1, currentPage + 1)), listenerOptions);
      viewport.addEventListener("scroll", update, { passive: true, signal: this.paginationController.signal });
      update();
    }

    async fetchForProducts(productIds, intent) {
      return Promise.all(productIds.map(async (productId) => {
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set("section_id", this.dataset.sectionId);
        url.searchParams.set("product_id", productId);
        // Request Shopify's maximum candidate pool so exclusions are less
        // likely to leave a partially filled recommendation carousel.
        url.searchParams.set("limit", "10");
        url.searchParams.set("intent", intent);

        const response = await fetch(url.toString(), { headers: { "X-Requested-With": "XMLHttpRequest" } });
        if (!response.ok) return [];
        const html = await response.text();
        const documentFragment = new DOMParser().parseFromString(html, "text/html");
        return Array.from(documentFragment.querySelectorAll("[data-recommendation-product-id]"));
      }));
    }
  }

  if (!customElements.get("custom-cart-plan")) customElements.define("custom-cart-plan", CustomCartPlan);
  if (!customElements.get("custom-cart-pack")) customElements.define("custom-cart-pack", CustomCartPack);
  if (!customElements.get("cart-bundle")) customElements.define("cart-bundle", CartBundle);
  if (!customElements.get("cart-recommendations")) customElements.define("cart-recommendations", CartRecommendations);

  const openDrawer = ({ refresh = false } = {}) => {
    const drawer = document.getElementById("cart-drawer");
    if (!drawer) return;
    if (refresh) document.dispatchEvent(new CustomEvent("cart:refresh"));
    window.setTimeout(() => drawer.show?.(), refresh ? 250 : 0);
  };

  document.addEventListener("sb:cart:added", () => openDrawer({ refresh: true }));
  document.addEventListener("cart:open", () => openDrawer({ refresh: true }));

  let pendingCartToggle;
  document.addEventListener("click", (event) => {
    const trigger = event.target.closest("[data-cart-trigger]");
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    trigger.setAttribute("aria-busy", "true");
    if (pendingCartToggle) return;

    pendingCartToggle = (async () => {
      await customElements.whenDefined("cart-drawer");
      const drawer = document.getElementById("cart-drawer");
      if (!drawer) return;
      if (drawer.hasAttribute("open")) await drawer.hide();
      else await drawer.show();
    })().catch((error) => {
      console.error("Cart drawer could not open:", error);
    }).finally(() => {
      pendingCartToggle = null;
      document.querySelectorAll("[data-cart-trigger]").forEach((button) => button.removeAttribute("aria-busy"));
    });
  }, true);

  document.documentElement.setAttribute("data-cart-handler-ready", "");
  const pendingCartTrigger = document.querySelector("[data-cart-pending]");
  document.querySelectorAll("[data-cart-pending]").forEach((trigger) => trigger.removeAttribute("data-cart-pending"));
  pendingCartTrigger?.click();

  document.addEventListener("click", (event) => {
    const variantChoice = event.target.closest("[data-recommendation-purchase-select-variant]");
    const oneTimeButton = event.target.closest("[data-recommendation-add-one-time]");
    const subscriptionToggle = event.target.closest("[data-recommendation-toggle-subscription]");
    const subscriptionOption = event.target.closest("[data-recommendation-add-subscription]");
    const control = variantChoice || oneTimeButton || subscriptionToggle || subscriptionOption;
    if (!control) return;

    const form = control.closest(".custom-cart__recommendation-purchase");
    if (!form || form.getAttribute("aria-busy") === "true") return;

    const variantInput = form.querySelector("[data-recommendation-purchase-variant]");
    const planInput = form.querySelector("[data-recommendation-purchase-plan]");
    const subscriptionMenu = form.querySelector("[data-recommendation-subscription-menu]");
    const toggle = form.querySelector("[data-recommendation-toggle-subscription]");
    if (!variantInput || !planInput) return;

    if (variantChoice) {
      event.preventDefault();
      const variantId = variantChoice.dataset.variantId;
      variantInput.value = variantId;

      const selectedLabel = form.querySelector("[data-recommendation-selected-variant]");
      const subscriptionLabel = form.querySelector("[data-recommendation-subscription-label]");
      const choices = form.querySelector(".custom-cart__purchase-choices");
      const variantPicker = form.querySelector("[data-recommendation-purchase-variant-picker]");
      const hasSubscription = variantChoice.dataset.hasSubscription === "true";
      const requiresSellingPlan = variantChoice.dataset.requiresSellingPlan === "true";

      if (selectedLabel) selectedLabel.textContent = variantChoice.dataset.variantTitle;
      const productCard = form.closest(".horizontal-product");
      const cardPrice = productCard?.querySelector("[data-recommendation-card-price]");
      const cardComparePrice = productCard?.querySelector("[data-recommendation-card-compare-price]");
      if (cardPrice) cardPrice.textContent = variantChoice.dataset.cardPrice;
      if (cardComparePrice) {
        const hasComparePrice = variantChoice.dataset.hasCompareAtPrice === "true";
        cardComparePrice.hidden = !hasComparePrice;
        if (hasComparePrice) cardComparePrice.textContent = variantChoice.dataset.cardCompareAtPrice;
      }
      const formOneTimeButton = form.querySelector("[data-recommendation-add-one-time]");
      if (formOneTimeButton) formOneTimeButton.hidden = requiresSellingPlan;
      if (toggle) toggle.hidden = !hasSubscription;
      if (subscriptionLabel) subscriptionLabel.textContent = variantChoice.dataset.subscriptionLabel;
      choices?.classList.toggle("is-subscription-only", requiresSellingPlan);
      form.querySelectorAll("[data-recommendation-purchase-select-variant]").forEach((button) => {
        button.classList.toggle("is-selected", button === variantChoice);
      });
      form.querySelectorAll("[data-recommendation-add-subscription]").forEach((button) => {
        button.hidden = button.dataset.variantId !== variantId;
      });
      if (subscriptionMenu) subscriptionMenu.hidden = true;
      if (toggle) toggle.setAttribute("aria-expanded", "false");
      if (variantPicker) variantPicker.open = false;
      return;
    }

    if (subscriptionToggle) {
      event.preventDefault();
      if (!subscriptionMenu) return;
      subscriptionMenu.hidden = !subscriptionMenu.hidden;
      subscriptionToggle.setAttribute("aria-expanded", String(!subscriptionMenu.hidden));
      return;
    }

    const reset = (errorEvent) => {
      form.removeAttribute("aria-busy");
      announce(errorEvent.detail?.error || "We couldn’t add that option. Please try again.");
    };
    form.setAttribute("aria-busy", "true");
    form.addEventListener("cart:error", reset, { once: true });

    if (subscriptionOption) {
      variantInput.value = subscriptionOption.dataset.variantId;
      planInput.disabled = false;
      planInput.value = subscriptionOption.dataset.sellingPlanId;
    } else {
      planInput.value = "";
      planInput.disabled = true;
    }

    form.requestSubmit();
  });

  document.addEventListener("click", (event) => {
    const option = event.target.closest("[data-recommendation-variant-option]");
    if (!option) return;

    const form = option.closest("form");
    if (!form) return;

    const picker = option.closest("[data-recommendation-variant-picker]");
    const variantInput = form.querySelector('input[name="id"]');
    if (!picker || !variantInput) return;

    const reset = () => {
      picker.removeAttribute("aria-busy");
      variantInput.value = "";
    };

    variantInput.value = option.dataset.recommendationVariantOption;
    picker.setAttribute("aria-busy", "true");
    form.addEventListener("cart:error", reset, { once: true });
    form.requestSubmit();
  });

  document.addEventListener("click", (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest("a[href]");
    if (!link || link.closest("#cart-drawer") || link.hasAttribute("data-custom-cart-navigate")) return;
    if (link.getAttribute("aria-controls") === "cart-drawer") return;
    if (window.themeVariables?.settings?.cartType !== "drawer" || window.themeVariables?.settings?.pageType === "cart") return;

    const linkUrl = new URL(link.href, window.location.origin);
    const cartUrl = new URL(`${root}cart`, window.location.origin);
    const cleanPath = (path) => path.replace(/\/+$/, "");
    if (linkUrl.origin !== window.location.origin || cleanPath(linkUrl.pathname) !== cleanPath(cartUrl.pathname)) return;

    event.preventDefault();
    openDrawer({ refresh: true });
  });
})();
